<template>
  <div
    v-show="dropdownActive"
    ref="dropdown"
    :style="cssStyles"
    class="select-items scroll-container"
    :class="{
      'select-items_small': small,
      'select-items_large': large
    }"
  >
    <slot />
  </div>
</template>

<script>
  const ACTIVE_CHUNK_SELECTOR = '.m-lazy-render__chunk_active';
  const ACTIVE_OPTION_SELECTOR = '.select-option_active';
  const DURATION_ANIMATION = 500;

  export default {
    name: 'BaseSelectDropdownOptions',
    props: {
      dropdownActive: {
        type: Boolean,
        required: true
      },
      small: {
        type: Boolean,
        default: false
      },
      large: {
        type: Boolean,
        default: false
      },
      width: {
        validator: prop => typeof prop === 'number' || prop === null,
        required: true
      },
      maxWidthOffset: {
        type: Number,
        default: 100
      },
      allowWidthOverflow: Boolean,
      isLoading: {
        type: Boolean,
        default: false
      },
      lazyRender: {
        type: Boolean,
        default: false
      },
      selectedItem: {
        type: Object,
        default: null
      }
    },
    computed: {
      cssStyles () {
        const styles = {
          minWidth: `${this.width}px`,
          maxWidth: `${this.width + this.maxWidthOffset}px`
        };

        if (!this.allowWidthOverflow) {
          styles.maxWidth = styles.minWidth;
        }

        return styles;
      },
      isContentOverflowed () {
        return this.$refs.dropdown.scrollHeight > this.$refs.dropdown.clientHeight;
      },
      canScrollToActive () {
        return this.dropdownActive && !this.isLoading && this.selectedItem;
      }
    },
    watch: {
      dropdownActive () {
        if (this.canScrollToActive) {
          this.scrollToActiveOption();
        }
      },
      isLoading () {
        if (this.canScrollToActive) {
          this.scrollToActiveOption();
        }
      }
    },
    methods: {
      scrollToActiveOption () {
        setTimeout(() => {
          if (this.isContentOverflowed) {
            let selector = '';
            let options = {};
            let defaultOptions = {
              container: this.$refs.dropdown
            };

            // show parent sticky header upon option after scroll
            if (this.selectedItem.parentId || this.selectedItem.isChoicesWithChildren) {
              let offsetHeight = this.large ? -36 : -30;

              const parent = this.$refs.dropdown.querySelector(`#${this.selectedItem.parentId}`);
              if (parent) {
                offsetHeight = -parent.clientHeight;
              }

              defaultOptions.offset = offsetHeight;
            }

            const lastVisibleChunksIds = [];

            this.$refs.dropdown.querySelectorAll('.m-lazy-render__chunk_visible').forEach(chunk => {
              lastVisibleChunksIds.push(Number(chunk.dataset.id));
            });

            /*
             *if chunk with active option isn`t visible now:
             * 1) First scroll to chunk and trigger it visibility
             * 2) onDone - scroll to active option after chunk is visible
             */
            if (this.lazyRender && !lastVisibleChunksIds.includes(this.selectedItem.chunkId)) {

              // if scrolling from below to active chunk
              if (lastVisibleChunksIds.some(chunkId => chunkId > this.selectedItem.chunkId)) {
                selector = `${ACTIVE_CHUNK_SELECTOR} + .m-lazy-render__chunk`;
                options = {...defaultOptions};
              } else {
                selector = ACTIVE_CHUNK_SELECTOR;
                options = {container: this.$refs.dropdown};
              }
              options = {
                ...options,
                onDone: () => setTimeout(() => {
                  this.$scrollUITo(
                    this.$refs.dropdown.querySelector(`${ACTIVE_CHUNK_SELECTOR} ${ACTIVE_OPTION_SELECTOR}`),
                    DURATION_ANIMATION,
                    defaultOptions
                  );
                }, 0)
              };
            } else {
              selector = ACTIVE_OPTION_SELECTOR;
              options = defaultOptions;
            }

            const toElem = this.$refs.dropdown.querySelector(selector);

            if (toElem) {
              this.$scrollUITo(toElem, DURATION_ANIMATION, options);
            }
          }
        }, 100);
      }
    }
  };
</script>

<style>
  .select-items {
    overflow-y: auto;

    position: relative;

    width: max-content;
    max-height: 346px;

    border-top: 4px solid var(--background-color);
    border-bottom: 4px solid var(--background-color);

    border-radius: var(--border-radius);

    transition: opacity var(--transition-duration);

    &.select-items_small {
      font-size: 12px;
    }

    &.select-items_large {
      font-size: 16px;
    }
  }

  .popper-fade-leave-to,
  .popper-fade-enter {
    transition: opacity var(--transition-duration);
  }

  .dropdown-enter,
  .dropdown-leave-to {
    opacity: 0.5;
    transform: scaleY(0);
  }

</style>
